import {
  getCurrentRuntimeHostname,
  getRuntimeEnvironment,
  inferRuntimeEnvironmentFromHostname,
} from "./runtimeEnvironment";

export const COLLABORATOR_PROGRAM_STORAGE_KEY =
  "mozzoapp:collaborator-program:local-state:v1";
export const COLLABORATOR_PROGRAM_SESSION_KEY =
  "mozzoapp:collaborator-program:local-session:v1";
export const COLLABORATOR_PROGRAM_CHANGED_EVENT =
  "mozzoapp:collaborator-program:local-changed";
export const COLLABORATOR_CONTRACT_VERSION =
  "mozzoapp-colaboradores-2026-09-v2";

const PRODUCTION_CLEAN_SLATE_STORAGE_KEY =
  "mozzoapp:collaborator-program:production-clean-slate:v1";
const PRODUCTION_CLEAN_SLATE_VERSION = "1";

export const COLLABORATOR_PROGRAM_RULES = Object.freeze({
  minimumCommissionableSales: 1500000,
  programCommissionRate: 0.006,
  restaurantSellerRate: 0.002,
  branchSellerRate: 0.001,
  parentStarTemporaryRate: 0.003,
  companyRestaurantRate: 0.004,
  companyTemporaryRate: 0.001,
  companyBranchRate: 0.005,
  temporaryStarCycles: Object.freeze([2, 3, 4]),
  normalPromotionLocationTarget: 30,
  starNetworkLocationTarget: 100,
  statementIssueDay: 5,
  invoiceDueDay: 10,
  paymentDueDay: 15,
});

const normalizeText = (value, maxLength = 180) =>
  String(value || "")
    .trim()
    .slice(0, maxLength);

const normalizeEmail = (value) => normalizeText(value, 180).toLowerCase();

const toMoney = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : 0;
};

const makeLocalId = (prefix) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const DEMO_NETWORK_PORTFOLIO_SPECS = Object.freeze([
  { memberId: "network-member-2", locationCount: 14 },
  { memberId: "network-member-3", locationCount: 13 },
  { memberId: "network-member-4", locationCount: 12 },
  { memberId: "network-member-5", locationCount: 11 },
  { memberId: "network-member-6", locationCount: 10 },
  { memberId: "network-member-7", locationCount: 34 },
]);

const DEMO_BUSINESS_NAMES = Object.freeze([
  "La Esquina",
  "Puerto Norte",
  "Buen Sabor",
  "Patio Central",
  "Estacion Gourmet",
  "Casa del Parque",
  "Mercado Sur",
  "Punto Urbano",
  "Distrito Centro",
  "Cocina Abierta",
  "Paseo del Rio",
  "Sabores del Litoral",
]);

const DEMO_BRANCH_NAMES = Object.freeze([
  "Casa central",
  "Centro",
  "Costanera",
]);

const DEMO_BUSINESS_TYPES = Object.freeze([
  "Restaurante",
  "Fast food",
  "Delivery",
  "Cafeteria",
]);

const createDemoNetworkPortfolios = () =>
  DEMO_NETWORK_PORTFOLIO_SPECS.map((spec, memberIndex) => ({
    memberId: spec.memberId,
    starProfileId: "collaborator-star-demo",
    locations: Array.from({ length: spec.locationCount }, (_, locationIndex) => {
      const companyIndex = Math.floor(locationIndex / 3);
      const branchIndex = locationIndex % 3;
      const locationType = branchIndex === 0 ? "restaurant" : "branch";
      const commissionableSales =
        3250000 + ((memberIndex * 13 + locationIndex * 7) % 42) * 125000;
      const collaboratorRate =
        locationType === "branch"
          ? COLLABORATOR_PROGRAM_RULES.branchSellerRate
          : COLLABORATOR_PROGRAM_RULES.restaurantSellerRate;

      return {
        id: `${spec.memberId}-location-${locationIndex + 1}`,
        networkMemberId: spec.memberId,
        companyName: `${
          DEMO_BUSINESS_NAMES[
            (companyIndex + memberIndex * 2) % DEMO_BUSINESS_NAMES.length
          ]
        } ${memberIndex + 1}`,
        branchName: DEMO_BRANCH_NAMES[branchIndex],
        restaurantEmail: `red${memberIndex + 2}.local${companyIndex + 1}@example.com`,
        businessType:
          DEMO_BUSINESS_TYPES[
            (companyIndex + memberIndex) % DEMO_BUSINESS_TYPES.length
          ],
        locationType,
        commissionableSales,
        collectedPlatformFee: toMoney(commissionableSales * 0.006),
        collaboratorRate,
        collaboratorCommission: toMoney(
          commissionableSales * collaboratorRate
        ),
        status: "active",
        linkedAt: `2026-${String(((memberIndex + companyIndex) % 7) + 1).padStart(
          2,
          "0"
        )}-${String(((locationIndex * 3) % 24) + 1).padStart(2, "0")}`,
      };
    }),
  }));

const getSafeStorage = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export const formatProgramRate = (rate) =>
  `${new Intl.NumberFormat("es-AR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(rate || 0) * 100)}%`;

export const isOperationalCollaboratorLocation = (
  location,
  settings = COLLABORATOR_PROGRAM_RULES
) =>
  toMoney(location?.commissionableSales) >=
    toMoney(
      settings?.minimumCommissionableSales ||
        COLLABORATOR_PROGRAM_RULES.minimumCommissionableSales
    ) &&
  toMoney(location?.collectedPlatformFee) > 0 &&
  location?.status !== "cancelled";

export const isFoundingStarCollaborator = (profile = {}) =>
  profile?.role === "star" &&
  !normalizeText(
    profile?.invitedByProfileId ?? profile?.invited_by_profile_id,
    180
  );

export const resolveStarBonusPortfolio = ({
  profile,
  networkOperationalLocationCount = 0,
  ownOperationalLocationCount = 0,
} = {}) => {
  const includesOwnLocations = isFoundingStarCollaborator(profile);
  const networkCount = Math.max(
    0,
    Math.floor(Number(networkOperationalLocationCount || 0))
  );
  const ownCount = Math.max(
    0,
    Math.floor(Number(ownOperationalLocationCount || 0))
  );

  return {
    includesOwnLocations,
    networkOperationalLocationCount: networkCount,
    ownOperationalLocationCount: ownCount,
    activeLocationCount:
      networkCount + (includesOwnLocations ? ownCount : 0),
  };
};

export const resolveStarNetworkBonus = ({
  activeLocationCount,
  previouslyActivated = false,
  lockedUntilTarget = false,
} = {}) => {
  const normalizedCount = Math.max(0, Math.floor(Number(activeLocationCount || 0)));
  const target = COLLABORATOR_PROGRAM_RULES.starNetworkLocationTarget;

  if (normalizedCount >= target) {
    return {
      rate: 0.1,
      activated: true,
      lockedUntilTarget: false,
    };
  }

  if (!previouslyActivated || lockedUntilTarget) {
    return {
      rate: 0,
      activated: Boolean(previouslyActivated),
      lockedUntilTarget: Boolean(lockedUntilTarget),
    };
  }

  if (normalizedCount <= 90) {
    return {
      rate: 0,
      activated: true,
      lockedUntilTarget: true,
    };
  }

  return {
    rate: (normalizedCount - 90) / 100,
    activated: true,
    lockedUntilTarget: false,
  };
};

const createProgramSettings = () => ({
  minimumCommissionableSales:
    COLLABORATOR_PROGRAM_RULES.minimumCommissionableSales,
  programCommissionRate: COLLABORATOR_PROGRAM_RULES.programCommissionRate,
  statementIssueDay: COLLABORATOR_PROGRAM_RULES.statementIssueDay,
  invoiceDueDay: COLLABORATOR_PROGRAM_RULES.invoiceDueDay,
  paymentDueDay: COLLABORATOR_PROGRAM_RULES.paymentDueDay,
  normalPromotionLocationTarget:
    COLLABORATOR_PROGRAM_RULES.normalPromotionLocationTarget,
  starNetworkLocationTarget:
    COLLABORATOR_PROGRAM_RULES.starNetworkLocationTarget,
});

export const getCollaboratorProgramRuntimeEnvironment = () => {
  const hostnameEnvironment = inferRuntimeEnvironmentFromHostname(
    getCurrentRuntimeHostname()
  );

  return hostnameEnvironment || getRuntimeEnvironment();
};

export const isCollaboratorProgramDemoMode = () =>
  getCollaboratorProgramRuntimeEnvironment() !== "production";

export const createEmptyCollaboratorProgramState = () => ({
  version: 1,
  programSettings: createProgramSettings(),
  profiles: [],
  locations: [],
  networkMembers: [],
  networkPortfolios: [],
  starNetworkStatus: {},
  settlements: [],
  invitations: [],
  updatedAt: new Date().toISOString(),
});

export const createInitialCollaboratorProgramState = () => ({
  version: 1,
  programSettings: createProgramSettings(),
  profiles: [
    {
      id: "collaborator-star-demo",
      role: "star",
      firstName: "Lucia",
      lastName: "Fernandez",
      email: "estrella@mozzoapp.local",
      phone: "+54 9 379 400 1200",
      cuit: "27-32123456-8",
      taxStatus: "Monotributo activo",
      payoutAlias: "lucia.mozzo",
      sellerCode: "MZ-EST-0042",
      status: "active",
      joinedAt: "2026-02-03",
    },
    {
      id: "collaborator-normal-demo",
      role: "normal",
      firstName: "Martin",
      lastName: "Gomez",
      email: "colaborador@mozzoapp.local",
      phone: "+54 9 379 400 1980",
      cuit: "20-33456789-4",
      taxStatus: "Monotributo activo",
      payoutAlias: "martin.mozzo",
      sellerCode: "MZ-COL-0187",
      status: "active",
      joinedAt: "2026-03-12",
      invitedByProfileId: "collaborator-star-demo",
    },
  ],
  locations: [
    {
      id: "location-star-1",
      ownerProfileId: "collaborator-star-demo",
      companyName: "Distrito Burger",
      branchName: "Casa central",
      restaurantEmail: "administracion@distritoburger.com",
      businessType: "Fast food",
      locationType: "restaurant",
      commissionableSales: 12850000,
      collectedPlatformFee: 77100,
      collaboratorRate: 0.002,
      collaboratorCommission: 25700,
      status: "active",
      linkedAt: "2026-02-11",
    },
    {
      id: "location-star-2",
      ownerProfileId: "collaborator-star-demo",
      companyName: "Distrito Burger",
      branchName: "Costanera",
      restaurantEmail: "administracion@distritoburger.com",
      businessType: "Fast food",
      locationType: "branch",
      commissionableSales: 5200000,
      collectedPlatformFee: 31200,
      collaboratorRate: 0.001,
      collaboratorCommission: 5200,
      status: "active",
      linkedAt: "2026-05-02",
    },
    {
      id: "location-star-3",
      ownerProfileId: "collaborator-star-demo",
      companyName: "Cafe Norte",
      branchName: "Centro",
      restaurantEmail: "gestion@cafenorte.com",
      businessType: "Cafeteria",
      locationType: "restaurant",
      commissionableSales: 2640000,
      collectedPlatformFee: 15840,
      collaboratorRate: 0.002,
      collaboratorCommission: 5280,
      status: "active",
      linkedAt: "2026-06-17",
    },
    {
      id: "location-star-4",
      ownerProfileId: "collaborator-star-demo",
      companyName: "Parrilla 1880",
      branchName: "Salon principal",
      restaurantEmail: "dueno@parrilla1880.com",
      businessType: "Restaurante",
      locationType: "restaurant",
      commissionableSales: 6100000,
      collectedPlatformFee: 0,
      collaboratorRate: 0.002,
      collaboratorCommission: 0,
      status: "fee_pending",
      linkedAt: "2026-07-04",
    },
    {
      id: "location-normal-1",
      ownerProfileId: "collaborator-normal-demo",
      companyName: "Casa Verde",
      branchName: "Centro",
      restaurantEmail: "operaciones@casaverde.com",
      businessType: "Restaurante",
      locationType: "restaurant",
      commissionableSales: 8400000,
      collectedPlatformFee: 50400,
      collaboratorRate: 0.002,
      collaboratorCommission: 16800,
      status: "active",
      linkedAt: "2026-04-21",
    },
    {
      id: "location-normal-2",
      ownerProfileId: "collaborator-normal-demo",
      companyName: "Casa Verde",
      branchName: "Terminal",
      restaurantEmail: "operaciones@casaverde.com",
      businessType: "Delivery",
      locationType: "branch",
      commissionableSales: 3600000,
      collectedPlatformFee: 21600,
      collaboratorRate: 0.001,
      collaboratorCommission: 3600,
      status: "active",
      linkedAt: "2026-06-08",
    },
  ],
  networkMembers: [
    {
      id: "network-member-1",
      starProfileId: "collaborator-star-demo",
      profileId: "collaborator-normal-demo",
      name: "Martin Gomez",
      email: "colaborador@mozzoapp.local",
      sellerCode: "MZ-COL-0187",
      activeLocationCount: 2,
      status: "active",
    },
    {
      id: "network-member-2",
      starProfileId: "collaborator-star-demo",
      name: "Carolina Benitez",
      email: "carolina@example.com",
      sellerCode: "MZ-COL-0194",
      activeLocationCount: 14,
      status: "active",
    },
    {
      id: "network-member-3",
      starProfileId: "collaborator-star-demo",
      name: "Nicolas Vera",
      email: "nicolas@example.com",
      sellerCode: "MZ-COL-0201",
      activeLocationCount: 13,
      status: "active",
    },
    {
      id: "network-member-4",
      starProfileId: "collaborator-star-demo",
      name: "Sofia Ramirez",
      email: "sofia@example.com",
      sellerCode: "MZ-COL-0210",
      activeLocationCount: 12,
      status: "active",
    },
    {
      id: "network-member-5",
      starProfileId: "collaborator-star-demo",
      name: "Federico Acosta",
      email: "federico@example.com",
      sellerCode: "MZ-COL-0227",
      activeLocationCount: 11,
      status: "active",
    },
    {
      id: "network-member-6",
      starProfileId: "collaborator-star-demo",
      name: "Valentina Ruiz",
      email: "valentina@example.com",
      sellerCode: "MZ-COL-0232",
      activeLocationCount: 10,
      status: "active",
    },
    {
      id: "network-member-7",
      starProfileId: "collaborator-star-demo",
      name: "Emiliano Silva",
      email: "emiliano@example.com",
      sellerCode: "MZ-COL-0246",
      activeLocationCount: 34,
      status: "active",
    },
  ],
  networkPortfolios: createDemoNetworkPortfolios(),
  starNetworkStatus: {
    "collaborator-star-demo": {
      previouslyActivated: true,
      lockedUntilTarget: false,
    },
  },
  settlements: [
    {
      id: "settlement-star-july-2026",
      profileId: "collaborator-star-demo",
      cycleLabel: "Julio 2026",
      cycleStart: "2026-07-01",
      cycleEnd: "2026-07-31",
      statementDate: "2026-08-05",
      invoiceDueDate: "2026-08-10",
      paymentDueDate: "2026-08-15",
      commissionableSales: 26800000,
      directCommission: 36180,
      starTemporaryCommission: 128400,
      networkBonus: 83400,
      creditNotes: -4160,
      netAmount: 243820,
      status: "awaiting_invoice",
    },
    {
      id: "settlement-star-june-2026",
      profileId: "collaborator-star-demo",
      cycleLabel: "Junio 2026",
      cycleStart: "2026-06-01",
      cycleEnd: "2026-06-30",
      statementDate: "2026-07-05",
      invoiceDueDate: "2026-07-10",
      paymentDueDate: "2026-07-15",
      commissionableSales: 22140000,
      directCommission: 29680,
      starTemporaryCommission: 104800,
      networkBonus: 66800,
      creditNotes: -2100,
      netAmount: 199180,
      status: "paid",
      invoiceNumber: "C 00002-00000118",
      invoiceSubmittedAt: "2026-07-08",
      paidAt: "2026-07-15",
      paymentReference: "MP-90318812",
    },
    {
      id: "settlement-normal-july-2026",
      profileId: "collaborator-normal-demo",
      cycleLabel: "Julio 2026",
      cycleStart: "2026-07-01",
      cycleEnd: "2026-07-31",
      statementDate: "2026-08-05",
      invoiceDueDate: "2026-08-10",
      paymentDueDate: "2026-08-15",
      commissionableSales: 12000000,
      directCommission: 20400,
      starTemporaryCommission: 0,
      networkBonus: 0,
      creditNotes: -900,
      netAmount: 19500,
      status: "awaiting_invoice",
    },
  ],
  invitations: [
    {
      id: "invitation-demo-1",
      starProfileId: "collaborator-star-demo",
      name: "Paula Sosa",
      email: "paula@example.com",
      code: "MZ-INV-7K2M",
      status: "pending",
      createdAt: "2026-08-04",
    },
  ],
  updatedAt: new Date().toISOString(),
});

const createDefaultCollaboratorProgramState = () =>
  isCollaboratorProgramDemoMode()
    ? createInitialCollaboratorProgramState()
    : createEmptyCollaboratorProgramState();

const ensureProductionCleanSlate = (storage) => {
  if (!storage || isCollaboratorProgramDemoMode()) {
    return;
  }

  if (
    storage.getItem(PRODUCTION_CLEAN_SLATE_STORAGE_KEY) ===
    PRODUCTION_CLEAN_SLATE_VERSION
  ) {
    return;
  }

  storage.removeItem(COLLABORATOR_PROGRAM_STORAGE_KEY);
  storage.removeItem(COLLABORATOR_PROGRAM_SESSION_KEY);
  storage.setItem(
    PRODUCTION_CLEAN_SLATE_STORAGE_KEY,
    PRODUCTION_CLEAN_SLATE_VERSION
  );
};

const normalizeLoadedState = (value) => {
  const version = Number(value?.version);

  if (
    !value ||
    typeof value !== "object" ||
    !Number.isInteger(version) ||
    version < 1 ||
    version > 2
  ) {
    return createDefaultCollaboratorProgramState();
  }

  const defaults = createDefaultCollaboratorProgramState();

  return {
    ...defaults,
    ...value,
    programSettings: {
      ...defaults.programSettings,
      ...(value.programSettings || {}),
    },
    profiles: Array.isArray(value.profiles) ? value.profiles : [],
    locations: Array.isArray(value.locations) ? value.locations : [],
    networkMembers: Array.isArray(value.networkMembers)
      ? value.networkMembers
      : [],
    networkPortfolios: Array.isArray(value.networkPortfolios)
      ? value.networkPortfolios
      : defaults.networkPortfolios,
    settlements: Array.isArray(value.settlements) ? value.settlements : [],
    invitations: Array.isArray(value.invitations) ? value.invitations : [],
  };
};

export const loadLocalCollaboratorProgramState = () => {
  const storage = getSafeStorage();

  if (!storage) {
    return createDefaultCollaboratorProgramState();
  }

  try {
    ensureProductionCleanSlate(storage);
    return normalizeLoadedState(
      JSON.parse(storage.getItem(COLLABORATOR_PROGRAM_STORAGE_KEY) || "null")
    );
  } catch {
    return createDefaultCollaboratorProgramState();
  }
};

export const saveLocalCollaboratorProgramState = (state) => {
  const storage = getSafeStorage();
  const normalizedState = {
    ...normalizeLoadedState(state),
    updatedAt: new Date().toISOString(),
  };

  if (storage) {
    try {
      storage.setItem(
        COLLABORATOR_PROGRAM_STORAGE_KEY,
        JSON.stringify(normalizedState)
      );
    } catch (error) {
      // Safari puede bloquear localStorage; Supabase sigue siendo la fuente real.
    }
  }

  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(COLLABORATOR_PROGRAM_CHANGED_EVENT, {
        detail: normalizedState,
      })
    );
  }

  return normalizedState;
};

export const mergeCollaboratorAccessState = (
  state,
  { profiles = [], invitations = [] } = {}
) => {
  const currentState = normalizeLoadedState(state);
  const incomingProfiles = Array.isArray(profiles) ? profiles : [];
  const incomingInvitations = Array.isArray(invitations) ? invitations : [];
  const profileIdRemap = {};
  const mergedProfiles = [...currentState.profiles];

  incomingProfiles.forEach((incomingProfile) => {
    const normalizedIncomingEmail = normalizeEmail(incomingProfile?.email);
    const existingIndex = mergedProfiles.findIndex(
      (profile) =>
        profile.id === incomingProfile?.id ||
        (normalizedIncomingEmail &&
          normalizeEmail(profile.email) === normalizedIncomingEmail)
    );

    if (existingIndex >= 0) {
      const existingProfile = mergedProfiles[existingIndex];
      if (
        existingProfile.id &&
        incomingProfile?.id &&
        existingProfile.id !== incomingProfile.id
      ) {
        profileIdRemap[existingProfile.id] = incomingProfile.id;
      }
      mergedProfiles[existingIndex] = {
        ...existingProfile,
        ...incomingProfile,
      };
      return;
    }

    if (incomingProfile?.id) {
      mergedProfiles.push(incomingProfile);
    }
  });

  const remapProfileId = (profileId) => profileIdRemap[profileId] || profileId;
  const mergedInvitations = [...currentState.invitations];

  incomingInvitations.forEach((incomingInvitation) => {
    const existingIndex = mergedInvitations.findIndex(
      (invitation) =>
        invitation.id === incomingInvitation?.id ||
        (invitation.code && invitation.code === incomingInvitation?.code) ||
        (normalizeEmail(invitation.email) ===
          normalizeEmail(incomingInvitation?.email) &&
          invitation.status === "pending")
    );
    const normalizedInvitation = {
      ...incomingInvitation,
      starProfileId: remapProfileId(incomingInvitation?.starProfileId),
      profileId: remapProfileId(incomingInvitation?.profileId),
    };

    if (existingIndex >= 0) {
      mergedInvitations[existingIndex] = {
        ...mergedInvitations[existingIndex],
        ...normalizedInvitation,
      };
      return;
    }

    if (incomingInvitation?.id) {
      mergedInvitations.push(normalizedInvitation);
    }
  });

  const nextStarNetworkStatus = Object.fromEntries(
    Object.entries(currentState.starNetworkStatus || {}).map(([profileId, value]) => [
      remapProfileId(profileId),
      value,
    ])
  );

  incomingProfiles
    .filter((profile) => profile?.role === "star")
    .forEach((profile) => {
      if (!nextStarNetworkStatus[profile.id]) {
        nextStarNetworkStatus[profile.id] = {
          previouslyActivated: false,
          lockedUntilTarget: false,
        };
      }
    });

  return saveLocalCollaboratorProgramState({
    ...currentState,
    profiles: mergedProfiles,
    invitations: mergedInvitations,
    locations: currentState.locations.map((location) => ({
      ...location,
      ownerProfileId: remapProfileId(location.ownerProfileId),
    })),
    networkMembers: currentState.networkMembers.map((member) => ({
      ...member,
      starProfileId: remapProfileId(member.starProfileId),
      profileId: remapProfileId(member.profileId),
    })),
    networkPortfolios: currentState.networkPortfolios.map((portfolio) => ({
      ...portfolio,
      starProfileId: remapProfileId(portfolio.starProfileId),
      profileId: remapProfileId(portfolio.profileId),
    })),
    settlements: currentState.settlements.map((settlement) => ({
      ...settlement,
      profileId: remapProfileId(settlement.profileId),
    })),
    starNetworkStatus: nextStarNetworkStatus,
  });
};

export const applyAcceptedCollaboratorAccess = (
  state,
  profile,
  invitation
) => {
  const nextState = mergeCollaboratorAccessState(state, {
    profiles: profile ? [profile] : [],
    invitations: invitation ? [invitation] : [],
  });

  if (!profile || profile.role !== "normal" || !profile.invitedByProfileId) {
    return nextState;
  }

  const existingMember = nextState.networkMembers.find(
    (member) =>
      member.profileId === profile.id ||
      normalizeEmail(member.email) === normalizeEmail(profile.email)
  );

  if (existingMember) {
    return saveLocalCollaboratorProgramState({
      ...nextState,
      networkMembers: nextState.networkMembers.map((member) =>
        member.id === existingMember.id
          ? {
              ...member,
              starProfileId: profile.invitedByProfileId,
              profileId: profile.id,
              name: `${profile.firstName} ${profile.lastName}`.trim(),
              email: profile.email,
              sellerCode: profile.sellerCode,
              status: profile.status,
            }
          : member
      ),
    });
  }

  return saveLocalCollaboratorProgramState({
    ...nextState,
    networkMembers: [
      ...nextState.networkMembers,
      {
        id: makeLocalId("network-member"),
        starProfileId: profile.invitedByProfileId,
        profileId: profile.id,
        name: `${profile.firstName} ${profile.lastName}`.trim(),
        email: profile.email,
        sellerCode: profile.sellerCode,
        activeLocationCount: 0,
        status: profile.status,
      },
    ],
  });
};

export const loadLocalCollaboratorSession = () => {
  const storage = getSafeStorage();

  if (!storage) {
    return null;
  }

  try {
    ensureProductionCleanSlate(storage);
    const session = JSON.parse(
      storage.getItem(COLLABORATOR_PROGRAM_SESSION_KEY) || "null"
    );
    return session?.profileId ? session : null;
  } catch {
    return null;
  }
};

export const saveLocalCollaboratorSession = (session) => {
  const storage = getSafeStorage();

  if (storage && session?.profileId) {
    try {
      storage.setItem(COLLABORATOR_PROGRAM_SESSION_KEY, JSON.stringify(session));
    } catch (error) {
      // La sesion autenticada de Supabase permite continuar aunque Safari no persista esta ayuda local.
    }
  }

  return session;
};

export const clearLocalCollaboratorSession = () => {
  try {
    getSafeStorage()?.removeItem(COLLABORATOR_PROGRAM_SESSION_KEY);
  } catch (error) {
    // No bloqueamos el cierre o la recuperacion de acceso por restricciones del navegador.
  }
};

export const resetLocalCollaboratorProgram = () => {
  const storage = getSafeStorage();
  storage?.removeItem(COLLABORATOR_PROGRAM_STORAGE_KEY);
  storage?.removeItem(COLLABORATOR_PROGRAM_SESSION_KEY);
  const initialState = createDefaultCollaboratorProgramState();

  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(COLLABORATOR_PROGRAM_CHANGED_EVENT, {
        detail: initialState,
      })
    );
  }

  return initialState;
};

export const getCollaboratorProfile = (state, profileId) =>
  (Array.isArray(state?.profiles) ? state.profiles : []).find(
    (profile) => profile.id === profileId
  ) || null;

export const hasAcceptedCurrentCollaboratorContract = (profile) =>
  profile?.contractAcceptance?.version === COLLABORATOR_CONTRACT_VERSION &&
  Boolean(profile?.contractAcceptance?.acceptedAt);

export const getCollaboratorNetworkMemberLocations = (state, memberOrId) => {
  const members = Array.isArray(state?.networkMembers)
    ? state.networkMembers
    : [];
  const member =
    typeof memberOrId === "string"
      ? members.find((candidate) => candidate.id === memberOrId)
      : memberOrId;

  if (!member) {
    return [];
  }

  if (member.profileId) {
    return (Array.isArray(state?.locations) ? state.locations : []).filter(
      (location) =>
        location.ownerProfileId === member.profileId &&
        (!location.networkStarProfileId ||
          location.networkStarProfileId === member.starProfileId)
    );
  }

  const portfolio = (
    Array.isArray(state?.networkPortfolios) ? state.networkPortfolios : []
  ).find((candidate) => candidate.memberId === member.id);

  return Array.isArray(portfolio?.locations) ? portfolio.locations : [];
};

export const summarizeCollaboratorLocations = (
  locations,
  settings = COLLABORATOR_PROGRAM_RULES
) => {
  const safeLocations = Array.isArray(locations) ? locations : [];
  const operationalLocations = safeLocations.filter((location) =>
    isOperationalCollaboratorLocation(location, settings)
  );
  const companyKeys = new Set(
    safeLocations.map((location) =>
      `${normalizeEmail(location.restaurantEmail)}|${normalizeText(
        location.companyName,
        120
      ).toLowerCase()}`
    )
  );

  return {
    companyCount: companyKeys.size,
    locationCount: safeLocations.length,
    operationalLocationCount: operationalLocations.length,
    commissionableSales: toMoney(
      safeLocations.reduce(
        (total, location) => total + toMoney(location.commissionableSales),
        0
      )
    ),
    collectedPlatformFee: toMoney(
      safeLocations.reduce(
        (total, location) => total + toMoney(location.collectedPlatformFee),
        0
      )
    ),
    collaboratorCommission: toMoney(
      safeLocations.reduce(
        (total, location) => total + toMoney(location.collaboratorCommission),
        0
      )
    ),
  };
};

export const createLocalDemoSession = (state, role = "star", email = "") => {
  const profiles = Array.isArray(state?.profiles) ? state.profiles : [];
  const normalizedEmail = normalizeEmail(email);
  const profile =
    profiles.find((candidate) => candidate.email === normalizedEmail) ||
    profiles.find((candidate) => candidate.role === role) ||
    profiles[0];

  if (!profile) {
    throw new Error("No encontramos un perfil local para abrir el portal.");
  }

  return saveLocalCollaboratorSession({
    profileId: profile.id,
    startedAt: new Date().toISOString(),
    localOnly: true,
  });
};

export const registerLocalCollaborator = (state, values = {}) => {
  const invitationCode = normalizeText(values.invitationCode, 40).toUpperCase();
  const email = normalizeEmail(values.email);
  const invitation = (state.invitations || []).find(
    (candidate) => candidate.code === invitationCode && candidate.status === "pending"
  );

  if (!invitation) {
    throw new Error("El codigo de invitacion no existe o ya fue utilizado.");
  }

  if (!email || email !== normalizeEmail(invitation.email)) {
    throw new Error("El correo debe coincidir con el que recibio la invitacion.");
  }

  if ((state.profiles || []).some((profile) => profile.email === email)) {
    throw new Error("Ese correo ya tiene un perfil de colaborador.");
  }

  const profileId = makeLocalId("collaborator");
  const sellerCode = `MZ-COL-${String(Date.now()).slice(-4)}`;
  const profile = {
    id: profileId,
    role: "normal",
    firstName: normalizeText(values.firstName, 80),
    lastName: normalizeText(values.lastName, 80),
    email,
    phone: normalizeText(values.phone, 50),
    cuit: normalizeText(values.cuit, 30),
    taxStatus: "Monotributo pendiente de validacion",
    payoutAlias: normalizeText(values.payoutAlias, 80),
    sellerCode,
    status: "pending_validation",
    joinedAt: new Date().toISOString().slice(0, 10),
    invitedByProfileId: invitation.starProfileId,
  };

  const nextState = {
    ...state,
    profiles: [...state.profiles, profile],
    invitations: state.invitations.map((candidate) =>
      candidate.id === invitation.id
        ? {
            ...candidate,
            status: "accepted",
            acceptedAt: new Date().toISOString(),
            profileId,
          }
        : candidate
    ),
    networkMembers: [
      ...state.networkMembers,
      {
        id: makeLocalId("network-member"),
        starProfileId: invitation.starProfileId,
        profileId,
        name: `${profile.firstName} ${profile.lastName}`.trim(),
        email,
        sellerCode,
        activeLocationCount: 0,
        status: "pending_validation",
      },
    ],
  };

  return {
    state: saveLocalCollaboratorProgramState(nextState),
    session: saveLocalCollaboratorSession({
      profileId,
      startedAt: new Date().toISOString(),
      localOnly: true,
    }),
    profile,
  };
};

export const linkLocalRestaurant = (state, profile, values = {}, origin = "") => {
  const restaurantEmail = normalizeEmail(values.restaurantEmail);
  const companyName = normalizeText(values.companyName, 120);

  if (!restaurantEmail || !companyName) {
    throw new Error("Completa el nombre comercial y el correo del comercio.");
  }

  const alreadyLinked = (state.locations || []).some(
    (location) =>
      normalizeEmail(location.restaurantEmail) === restaurantEmail &&
      normalizeText(location.branchName, 120).toLowerCase() ===
        normalizeText(values.branchName || "Casa central", 120).toLowerCase()
  );

  if (alreadyLinked) {
    throw new Error("Ese correo y esa sucursal ya estan vinculados.");
  }

  const referralId = makeLocalId("restaurant-referral");
  const locationType = values.locationType === "branch" ? "branch" : "restaurant";
  const normalizedOrigin = normalizeText(origin, 240).replace(/\/$/, "");
  const onboardingUrl = `${normalizedOrigin}/login?register=1&email=${encodeURIComponent(
    restaurantEmail
  )}&seller=${encodeURIComponent(profile.sellerCode)}&ref=${encodeURIComponent(
    referralId
  )}`;
  const location = {
    id: referralId,
    ownerProfileId: profile.id,
    companyName,
    branchName: normalizeText(values.branchName, 120) || "Casa central",
    restaurantEmail,
    businessType: normalizeText(values.businessType, 80) || "Restaurante",
    locationType,
    commissionableSales: 0,
    collectedPlatformFee: 0,
    collaboratorRate:
      locationType === "branch"
        ? COLLABORATOR_PROGRAM_RULES.branchSellerRate
        : COLLABORATOR_PROGRAM_RULES.restaurantSellerRate,
    collaboratorCommission: 0,
    status: "prelinked",
    linkedAt: new Date().toISOString().slice(0, 10),
    onboardingUrl,
  };

  return {
    state: saveLocalCollaboratorProgramState({
      ...state,
      locations: [location, ...state.locations],
    }),
    location,
  };
};

export const createLocalCollaboratorInvitation = (
  state,
  starProfile,
  values = {},
  origin = ""
) => {
  if (starProfile?.role !== "star") {
    throw new Error("Solo un colaborador estrella puede emitir invitaciones.");
  }

  const email = normalizeEmail(values.email);
  const name = normalizeText(values.name, 140);

  if (!email || !name) {
    throw new Error("Completa nombre y correo del nuevo colaborador.");
  }

  if (
    (state.profiles || []).some((profile) => profile.email === email) ||
    (state.invitations || []).some(
      (invitation) => invitation.email === email && invitation.status === "pending"
    )
  ) {
    throw new Error("Ese correo ya pertenece al programa o tiene una invitacion pendiente.");
  }

  const code = `MZ-INV-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
  const invitation = {
    id: makeLocalId("invitation"),
    starProfileId: starProfile.id,
    name,
    email,
    code,
    status: "pending",
    createdAt: new Date().toISOString().slice(0, 10),
  };
  const normalizedOrigin = normalizeText(origin, 240).replace(/\/$/, "");

  return {
    state: saveLocalCollaboratorProgramState({
      ...state,
      invitations: [invitation, ...state.invitations],
    }),
    invitation: {
      ...invitation,
      registrationUrl: `${normalizedOrigin}/colaboradores/registro?code=${encodeURIComponent(
        code
      )}&email=${encodeURIComponent(email)}`,
    },
  };
};

export const registerLocalSettlementInvoice = (state, profile, values = {}) => {
  const settlementId = normalizeText(values.settlementId, 120);
  const invoiceNumber = normalizeText(values.invoiceNumber, 80);
  const settlement = (state.settlements || []).find(
    (candidate) =>
      candidate.id === settlementId && candidate.profileId === profile?.id
  );

  if (!settlement || settlement.status !== "awaiting_invoice") {
    throw new Error("La liquidacion seleccionada ya no admite una nueva factura.");
  }

  if (!invoiceNumber) {
    throw new Error("Ingresa el numero completo de la factura.");
  }

  const submittedAt = new Date().toISOString();
  const submittedDate = submittedAt.slice(0, 10);
  const submittedLate = submittedDate > settlement.invoiceDueDate;

  return saveLocalCollaboratorProgramState({
    ...state,
    settlements: state.settlements.map((candidate) =>
      candidate.id === settlement.id
        ? {
            ...candidate,
            status: submittedLate ? "invoice_late" : "invoice_submitted",
            invoiceNumber,
            invoiceSubmittedAt: submittedDate,
            invoiceFileName: normalizeText(values.invoiceFileName, 180),
          }
        : candidate
    ),
  });
};

export const updateLocalCollaboratorProfile = (state, profileId, values = {}) =>
  saveLocalCollaboratorProgramState({
    ...state,
    profiles: state.profiles.map((profile) =>
      profile.id === profileId
        ? {
            ...profile,
            firstName: normalizeText(values.firstName ?? profile.firstName, 80),
            lastName: normalizeText(values.lastName ?? profile.lastName, 80),
            phone: normalizeText(values.phone ?? profile.phone, 50),
            cuit: normalizeText(values.cuit ?? profile.cuit, 30),
            taxCondition: normalizeText(
              values.taxCondition ?? profile.taxCondition,
              80
            ),
            fiscalAddress: normalizeText(
              values.fiscalAddress ?? profile.fiscalAddress,
              160
            ),
            taxStatus: normalizeText(values.taxStatus ?? profile.taxStatus, 120),
            payoutAlias: normalizeText(
              values.payoutAlias ?? profile.payoutAlias,
              80
            ),
          }
        : profile
    ),
  });

export const acceptLocalCollaboratorContract = (
  state,
  profileId,
  values = {}
) => {
  const profile = getCollaboratorProfile(state, profileId);

  if (!profile) {
    throw new Error("No encontramos el colaborador para aceptar el contrato.");
  }

  const firstName = normalizeText(values.firstName ?? profile.firstName, 80);
  const lastName = normalizeText(values.lastName ?? profile.lastName, 80);
  const cuit = normalizeText(values.cuit ?? profile.cuit, 30);
  const phone = normalizeText(values.phone ?? profile.phone, 50);
  const taxCondition = normalizeText(values.taxCondition, 80);
  const fiscalAddress = normalizeText(values.fiscalAddress, 160);
  const payoutAlias = normalizeText(values.payoutAlias ?? profile.payoutAlias, 80);

  if (
    !firstName ||
    !lastName ||
    !cuit ||
    !phone ||
    !taxCondition ||
    !fiscalAddress ||
    !payoutAlias
  ) {
    throw new Error("Completa tus datos personales, fiscales y de cobro.");
  }

  if (
    !values.acceptsContract ||
    !values.acceptsIndependentRelationship ||
    !values.acceptsInvoiceRequirement
  ) {
    throw new Error("Debes aceptar el contrato, la relacion independiente y la obligacion de facturar.");
  }

  const acceptedAt = new Date().toISOString();
  const browser =
    typeof navigator === "undefined"
      ? {}
      : {
          userAgent: normalizeText(navigator.userAgent, 320),
          language: normalizeText(navigator.language, 40),
        };
  const timezone =
    typeof Intl === "undefined"
      ? ""
      : normalizeText(Intl.DateTimeFormat().resolvedOptions().timeZone, 80);
  const origin =
    typeof window === "undefined" ? "" : normalizeText(window.location.origin, 160);

  const nextState = {
    ...state,
    profiles: state.profiles.map((candidate) =>
      candidate.id === profileId
        ? {
            ...candidate,
            firstName,
            lastName,
            cuit,
            phone,
            taxCondition,
            fiscalAddress,
            taxStatus:
              taxCondition === "monotributo"
                ? "Monotributo declarado por el colaborador"
                : taxCondition === "responsable_inscripto"
                  ? "Responsable inscripto declarado por el colaborador"
                  : "Condicion fiscal declarada por el colaborador",
            payoutAlias,
            contractAcceptance: {
              version: COLLABORATOR_CONTRACT_VERSION,
              acceptedAt,
              acceptedDate: acceptedAt.slice(0, 10),
              acceptedFrom: "collaborator_portal",
              contractHash: "mz-colaboradores-v1-clickwrap",
              origin,
              timezone,
              ...browser,
            },
          }
        : candidate
    ),
    networkMembers: state.networkMembers.map((member) =>
      member.profileId === profileId
        ? {
            ...member,
            name: `${firstName} ${lastName}`.trim(),
            contractAcceptedAt: acceptedAt,
          }
        : member
    ),
  };

  return saveLocalCollaboratorProgramState(nextState);
};

export const createLocalStarCollaborator = (state, values = {}) => {
  const firstName = normalizeText(values.firstName, 80);
  const lastName = normalizeText(values.lastName, 80);
  const email = normalizeEmail(values.email);
  const cuit = normalizeText(values.cuit, 30);

  if (!firstName || !lastName || !email || !cuit) {
    throw new Error("Completa nombre, apellido, correo y CUIT.");
  }

  if ((state.profiles || []).some((profile) => profile.email === email)) {
    throw new Error("Ese correo ya pertenece a un colaborador.");
  }

  const profile = {
    id: values.id || makeLocalId("collaborator-star"),
    role: "star",
    firstName,
    lastName,
    email,
    phone: normalizeText(values.phone, 50),
    cuit,
    taxStatus:
      normalizeText(values.taxStatus, 120) ||
      (values.activateNow
        ? "Monotributo activo"
        : "Monotributo pendiente de validacion"),
    payoutAlias: normalizeText(values.payoutAlias, 80),
    sellerCode:
      normalizeText(values.sellerCode, 40) ||
      `MZ-EST-${String(Date.now()).slice(-4)}`,
    status:
      normalizeText(values.status, 40) ||
      (values.activateNow ? "active" : "pending_validation"),
    joinedAt:
      normalizeText(values.joinedAt, 40).slice(0, 10) ||
      new Date().toISOString().slice(0, 10),
    ...(values.userId ? { userId: values.userId } : {}),
  };

  const invitation = values.invitation
    ? {
        ...values.invitation,
        profileId: values.invitation.profileId || profile.id,
      }
    : null;

  return {
    state: saveLocalCollaboratorProgramState({
      ...state,
      profiles: [profile, ...state.profiles],
      invitations: invitation
        ? [invitation, ...state.invitations]
        : state.invitations,
      starNetworkStatus: {
        ...state.starNetworkStatus,
        [profile.id]: {
          previouslyActivated: false,
          lockedUntilTarget: false,
        },
      },
    }),
    profile,
    invitation,
  };
};

export const updateLocalCollaboratorStatus = (
  state,
  profileId,
  nextStatus
) => {
  const allowedStatuses = new Set([
    "active",
    "pending_validation",
    "suspended",
  ]);

  if (!allowedStatuses.has(nextStatus)) {
    throw new Error("El estado solicitado no es valido.");
  }

  const profile = getCollaboratorProfile(state, profileId);

  if (!profile) {
    throw new Error("No encontramos el colaborador seleccionado.");
  }

  const taxStatus =
    nextStatus === "active"
      ? "Monotributo activo"
      : nextStatus === "suspended"
        ? "Monotributo suspendido"
        : "Monotributo pendiente de validacion";

  return saveLocalCollaboratorProgramState({
    ...state,
    profiles: state.profiles.map((candidate) =>
      candidate.id === profileId
        ? {
            ...candidate,
            status: nextStatus,
            taxStatus,
            reviewedAt: new Date().toISOString(),
          }
        : candidate
    ),
    networkMembers: state.networkMembers.map((member) =>
      member.profileId === profileId
        ? { ...member, status: nextStatus }
        : member
    ),
  });
};

export const updateLocalCollaboratorLocationStatus = (
  state,
  locationId,
  nextStatus
) => {
  const allowedStatuses = new Set([
    "active",
    "fee_pending",
    "prelinked",
    "cancelled",
  ]);

  if (!allowedStatuses.has(nextStatus)) {
    throw new Error("El estado de la sucursal no es valido.");
  }

  if (!(state.locations || []).some((location) => location.id === locationId)) {
    throw new Error("No encontramos la sucursal seleccionada.");
  }

  return saveLocalCollaboratorProgramState({
    ...state,
    locations: state.locations.map((location) =>
      location.id === locationId
        ? {
            ...location,
            status: nextStatus,
            statusUpdatedAt: new Date().toISOString(),
          }
        : location
    ),
  });
};

export const approveLocalSettlementInvoice = (state, settlementId) => {
  const settlement = (state.settlements || []).find(
    (candidate) => candidate.id === settlementId
  );

  if (
    !settlement ||
    !["invoice_submitted", "invoice_late"].includes(settlement.status)
  ) {
    throw new Error("La liquidacion no tiene una factura pendiente de aprobacion.");
  }

  return saveLocalCollaboratorProgramState({
    ...state,
    settlements: state.settlements.map((candidate) =>
      candidate.id === settlementId
        ? {
            ...candidate,
            status: "scheduled",
            invoiceApprovedAt: new Date().toISOString(),
            paymentScheduledForNextCycle: settlement.status === "invoice_late",
          }
        : candidate
    ),
  });
};

export const markLocalSettlementPaid = (
  state,
  settlementId,
  paymentReference
) => {
  const normalizedReference = normalizeText(paymentReference, 120);
  const settlement = (state.settlements || []).find(
    (candidate) => candidate.id === settlementId
  );

  if (!settlement || settlement.status !== "scheduled") {
    throw new Error("La liquidacion debe estar programada antes de pagarla.");
  }

  if (!normalizedReference) {
    throw new Error("Ingresa la referencia de la transferencia.");
  }

  return saveLocalCollaboratorProgramState({
    ...state,
    settlements: state.settlements.map((candidate) =>
      candidate.id === settlementId
        ? {
            ...candidate,
            status: "paid",
            paidAt: new Date().toISOString().slice(0, 10),
            paymentReference: normalizedReference,
          }
        : candidate
    ),
  });
};

export const updateLocalCollaboratorProgramSettings = (state, values = {}) => {
  const minimumCommissionableSales = toMoney(
    values.minimumCommissionableSales
  );
  const statementIssueDay = Math.floor(Number(values.statementIssueDay));
  const invoiceDueDay = Math.floor(Number(values.invoiceDueDay));
  const paymentDueDay = Math.floor(Number(values.paymentDueDay));

  if (minimumCommissionableSales <= 0) {
    throw new Error("El minimo comisionable debe ser mayor que cero.");
  }

  if (
    ![statementIssueDay, invoiceDueDay, paymentDueDay].every(
      (day) => Number.isInteger(day) && day >= 1 && day <= 28
    )
  ) {
    throw new Error("Las fechas administrativas deben estar entre los dias 1 y 28.");
  }

  if (!(statementIssueDay < invoiceDueDay && invoiceDueDay < paymentDueDay)) {
    throw new Error("La emision, la factura y el pago deben quedar en orden cronologico.");
  }

  return saveLocalCollaboratorProgramState({
    ...state,
    programSettings: {
      ...state.programSettings,
      minimumCommissionableSales,
      statementIssueDay,
      invoiceDueDay,
      paymentDueDay,
    },
  });
};
